import { useState } from 'react';
import { X, Star } from 'lucide-react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (score: number, comment?: string | null) => Promise<void> | void;
  isSubmitting?: boolean;
};

export default function SessionFeedbackModal({ isOpen, onClose, onSubmit, isSubmitting }: Props) {
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState<string>('');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-black/5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">Rate This Coaching Session</h2>
          <button onClick={onClose} className="rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200" aria-label="Close feedback">
            <X size={16} />
          </button>
        </div>
        <p className="mb-4 text-sm text-slate-600">How likely are you to recommend this coaching session to a colleague? (0-10)</p>

        <div className="mb-4 grid grid-cols-11 gap-1">
          {Array.from({ length: 11 }, (_, i) => i).map((n) => (
            <button
              key={n}
              onClick={() => setScore(n)}
              className={`h-9 rounded-md text-xs font-semibold ${score === n ? 'bg-[#4C62E3] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              aria-label={`Score ${n}`}
            >
              {n}
            </button>
          ))}
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Optional: What stood out or what could be improved?"
          className="mb-4 h-24 w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm placeholder-slate-400 focus:border-royal-blue focus:outline-none focus:ring-2 focus:ring-royal-blue/20"
        />

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Not Now</button>
          <button
            onClick={() => score !== null && onSubmit(score, comment || undefined)}
            disabled={isSubmitting || score === null}
            className="rounded-lg bg-[#4C62E3] px-4 py-2 text-sm font-semibold text-white hover:bg-[#3B4CC5] disabled:opacity-60"
          >
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
